import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Copy, Check, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Code de parrainage, filleuls et crédit mensuel. */
export default function CarteParrainage({ className = "" }) {
  const { lang } = useI18n();
  const fr = lang !== "en";
  const [etat, setEtat] = useState(null);
  const [copie, setCopie] = useState("");
  // Langue des modèles du kit de parrainage (indépendante de l'interface : un
  // client francophone peut vouloir écrire à un contact anglophone).
  const [langueKit, setLangueKit] = useState(["en", "fr", "es"].includes(lang) ? lang : "en");

  useEffect(() => {
    api.get("/parrainage").then(({ data }) => setEtat(data)).catch(() => setEtat(false));
  }, []);

  const copier = (texte, quoi) => {
    navigator.clipboard.writeText(texte).then(() => {
      setCopie(quoi);
      toast.success(fr ? "Copié" : "Copied");
      setTimeout(() => setCopie(""), 2000);
    });
  };

  const argent = (v) =>
    fr ? `${Number(v || 0).toFixed(2).replace(".", ",")} $` : `$${Number(v || 0).toFixed(2)}`;

  if (etat === false) return null;
  if (!etat) {
    return (
      <div className={className}>
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-brand-ink" /></div>
      </div>
    );
  }

  // Modèles prêts à envoyer, code et lien du client déjà insérés. Les crochets
  // [ ] sont les seuls champs que le client remplit (le contact, son propre nom).
  const CODE = etat.code;
  const LIEN = etat.lien;
  const KIT = {
    en: {
      nom: "English",
      sujet: "quoting roofs in under a minute — thought of you",
      courriel: `Hey [first name],

Not a sales pitch — just something that's actually saved me time. I've been using Roof Border to price roofs straight from the address: satellite measures the roof, it applies my prices, and it spits out a branded PDF in under a minute. I close more because I'm the first to get back to people with a number.

If you want to try it, use my code ${CODE} — you get 20% off your first three months, and it gives me a small credit too. No catch.

Here's the link with the code already in: ${LIEN}

[your name]
[your company]`,
      sms: `Hey [first name], been using Roof Border to quote roofs from just the address — under a minute, branded PDF. Use my code ${CODE} for 20% off 3 months: ${LIEN}`,
      linkedin: `Hey [first name] — been pricing roofs straight from the address with Roof Border (satellite measure + my prices + a branded PDF in under a minute). If you want to try it, my code ${CODE} gets you 20% off for 3 months: ${LIEN}`,
    },
    fr: {
      nom: "Français",
      sujet: "chiffrer un toit en moins d'une minute — j'ai pensé à toi",
      courriel: `Salut [prénom],

C'est pas une annonce, juste un truc qui me fait vraiment gagner du temps. J'utilise Roof Border pour chiffrer mes toits directement à partir de l'adresse : le satellite mesure le toit, ça applique mes prix, pis ça sort une soumission PDF à mon image en moins d'une minute. Je ferme plus de contrats parce que je suis le premier à rappeler avec un prix.

Si tu veux l'essayer, prends mon code ${CODE} — t'as 20 % de rabais sur tes trois premiers mois, pis ça me donne un petit crédit aussi. Pas de piège.

Le lien avec le code déjà entré : ${LIEN}

[ton nom]
[ton entreprise]`,
      sms: `Salut [prénom], j'utilise Roof Border pour chiffrer mes toits juste avec l'adresse — moins d'une minute, soumission PDF à mon image. Prends mon code ${CODE} pour 20 % de rabais 3 mois : ${LIEN}`,
      linkedin: `Salut [prénom] — je chiffre mes toits direct à partir de l'adresse avec Roof Border (mesure satellite + mes prix + une soumission PDF en moins d'une minute). Si tu veux l'essayer, mon code ${CODE} te donne 20 % de rabais pendant 3 mois : ${LIEN}`,
    },
    es: {
      nom: "Español",
      sujet: "cotizar un techo en menos de un minuto — pensé en ti",
      courriel: `Hola [nombre],

No es publicidad, solo algo que de verdad me ahorra tiempo. Uso Roof Border para cotizar techos directamente desde la dirección: el satélite mide el techo, aplica mis precios y genera una cotización en PDF con mi marca en menos de un minuto. Cierro más contratos porque soy el primero en responder con un precio.

Si quieres probarlo, usa mi código ${CODE} — obtienes 20 % de descuento tus primeros tres meses, y a mí me da un crédito también. Sin trucos.

El enlace con el código ya puesto: ${LIEN}

[tu nombre]
[tu empresa]`,
      sms: `Hola [nombre], uso Roof Border para cotizar techos solo con la dirección — menos de un minuto, cotización en PDF con mi marca. Usa mi código ${CODE} para 20 % de descuento 3 meses: ${LIEN}`,
      linkedin: `Hola [nombre] — cotizo techos directo desde la dirección con Roof Border (medición satelital + mis precios + una cotización PDF en menos de un minuto). Si quieres probarlo, mi código ${CODE} te da 20 % de descuento por 3 meses: ${LIEN}`,
    },
  };
  const K = KIT[langueKit] || KIT.en;
  const trad = (frTxt, enTxt, esTxt) => (fr ? frTxt : lang === "es" ? esTxt : enTxt);

  return (
    <div className={className}>
      <h3 className="font-heading font-bold text-lg mb-1 flex items-center gap-2">
        <Gift className="h-4 w-4 text-brand-ink" />
        {fr ? "Parrainage" : "Referrals"}
      </h3>
      <p className="text-sm text-ink-soft mb-5">
        {fr
          ? `Donnez votre code à un couvreur d'une autre région : il obtient ${etat.filleul_percent} % de rabais pendant ${etat.filleul_mois} mois, et vous touchez ${etat.part_parrain} % de SON forfait en crédit, chaque mois, tant qu'il reste abonné.`
          : `Give your code to a roofer in another region: they get ${etat.filleul_percent}% off for ${etat.filleul_mois} months, and you earn ${etat.part_parrain}% of THEIR plan as credit, every month, for as long as they stay subscribed.`}
      </p>

      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <div className="bg-field border border-line rounded-md p-4">
          <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">
            {fr ? "Crédit par mois" : "Credit per month"}
          </div>
          <div className={`font-heading font-extrabold text-3xl tracking-tighter ${etat.couvert ? "text-emerald-500" : "text-brand-ink"}`}>
            {argent(etat.credit_mensuel)}
          </div>
          {etat.couvert && (
            <div className="text-xs text-emerald-500 mt-1">
              {fr ? "Votre abonnement est couvert" : "Your subscription is covered"}
            </div>
          )}
        </div>
        <div className="bg-field border border-line rounded-md p-4">
          <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">
            {fr ? "Filleuls actifs" : "Active referrals"}
          </div>
          <div className="font-heading font-extrabold text-3xl tracking-tighter">{etat.actifs}</div>
          {!etat.couvert && etat.mon_forfait > 0 && (
            <div className="text-xs text-ink-faint mt-1">
              {fr
                ? `${argent(Math.max(0, etat.mon_forfait - etat.credit_mensuel))} avant la gratuité`
                : `${argent(Math.max(0, etat.mon_forfait - etat.credit_mensuel))} to go free`}
            </div>
          )}
        </div>
        <div className="bg-field border border-line rounded-md p-4">
          <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">
            {fr ? "Votre code" : "Your code"}
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="code-parrain" className="font-mono font-bold text-lg tracking-widest">{etat.code}</span>
            <button onClick={() => copier(etat.code, "code")} title={fr ? "Copier" : "Copy"}
                    className="p-1.5 rounded bg-hover hover:bg-hover-strong transition-colors">
              {copie === "code" ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Le crédit ne sort jamais en argent : il faut le dire clairement. */}
      <div className="bg-field border border-line rounded-md p-4 mb-5 flex flex-wrap gap-x-10 gap-y-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">
            {fr ? "Solde disponible" : "Available balance"}
          </div>
          <div className="font-heading font-bold text-xl">{argent(etat.credit_solde)}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-ink-faint mb-1">
            {fr ? "Crédit versé à ce jour" : "Credit earned to date"}
          </div>
          <div className="font-heading font-bold text-xl">{argent(etat.credit_verse)}</div>
        </div>
        <p className="text-xs text-ink-faint flex-1 min-w-[240px] self-center">
          {fr
            ? "Le crédit se déduit automatiquement de vos prochaines factures — abonnement, estimations en dépassement, changement de forfait. Il n'est pas remboursable en argent."
            : "Credit comes off your next invoices automatically — subscription, overage estimates, plan changes. It cannot be paid out in cash."}
        </p>
      </div>

      <div className="mb-5">
        <div className="text-xs uppercase tracking-widest text-ink-faint mb-2">
          {fr ? "Lien à partager" : "Link to share"}
        </div>
        <div className="flex gap-2">
          <input readOnly value={etat.lien} onFocus={(e) => e.target.select()}
                 className="flex-1 bg-field border border-line-strong rounded-md px-3 py-2 text-sm text-ink-soft outline-none" />
          <Button onClick={() => copier(etat.lien, "lien")}
                  className="bg-hover-strong hover:bg-line-strong text-ink rounded-md shrink-0">
            {copie === "lien" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Kit de parrainage : modèles prêts à envoyer, code déjà inséré. */}
      <div className="border-t border-line pt-5 mb-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <div className="text-xs uppercase tracking-widest text-ink-faint">
            {trad("Kit à envoyer", "Ready-to-send kit", "Kit para enviar")}
          </div>
          {/* Langue des modèles, indépendante de l'interface. */}
          <div className="inline-flex rounded-md border border-line-strong overflow-hidden">
            {["en", "fr", "es"].map((code) => (
              <button
                key={code}
                type="button"
                data-testid={`kit-langue-${code}`}
                onClick={() => setLangueKit(code)}
                className={`px-2.5 py-1 text-xs font-bold transition-colors ${
                  langueKit === code ? "bg-orange-600 text-white" : "bg-field text-ink-soft hover:bg-hover"
                }`}
              >
                {KIT[code].nom}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-ink-faint mb-4">
          {trad(
            "Envoyez-le à un couvreur que vous connaissez. Remplacez les [crochets] ; votre code et votre lien sont déjà insérés.",
            "Send it to a roofer you know. Replace the [brackets]; your code and link are already filled in.",
            "Envíelo a un techador que conozca. Reemplace los [corchetes]; su código y su enlace ya están incluidos."
          )}
        </p>

        {/* Courriel : objet + corps, chacun copiable. */}
        <div className="bg-field border border-line rounded-md p-3 mb-3">
          <div className="text-[11px] uppercase tracking-widest text-ink-faint mb-1.5">
            {trad("Courriel", "Email", "Correo")}
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-ink-faint shrink-0">{trad("Objet :", "Subject:", "Asunto:")}</span>
            <input readOnly value={K.sujet} onFocus={(e) => e.target.select()}
                   className="flex-1 min-w-0 bg-app border border-line rounded px-2 py-1 text-xs text-ink-soft outline-none" />
            <button onClick={() => copier(K.sujet, "kit-sujet")} title={fr ? "Copier" : "Copy"}
                    className="p-1.5 rounded bg-hover hover:bg-hover-strong transition-colors shrink-0">
              {copie === "kit-sujet" ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
          <div className="relative">
            <textarea readOnly value={K.courriel} rows={10} onFocus={(e) => e.target.select()}
                      className="w-full bg-app border border-line rounded px-3 py-2 text-xs text-ink-soft outline-none resize-y font-sans leading-relaxed" />
            <Button onClick={() => copier(K.courriel, "kit-courriel")}
                    className="absolute top-2 right-2 h-7 px-2 bg-hover-strong hover:bg-line-strong text-ink rounded text-xs">
              {copie === "kit-courriel" ? <Check className="h-3.5 w-3.5" /> : <><Copy className="h-3.5 w-3.5 mr-1" />{fr ? "Copier" : "Copy"}</>}
            </Button>
          </div>
        </div>

        {/* Texto et LinkedIn : un bloc copiable chacun. */}
        {[
          { cle: "sms", titre: trad("Texto / SMS", "Text / SMS", "Texto / SMS"), val: K.sms, rows: 3 },
          { cle: "linkedin", titre: "LinkedIn", val: K.linkedin, rows: 3 },
        ].map((b) => (
          <div key={b.cle} className="bg-field border border-line rounded-md p-3 mb-3">
            <div className="text-[11px] uppercase tracking-widest text-ink-faint mb-1.5">{b.titre}</div>
            <div className="relative">
              <textarea readOnly value={b.val} rows={b.rows} onFocus={(e) => e.target.select()}
                        className="w-full bg-app border border-line rounded px-3 py-2 text-xs text-ink-soft outline-none resize-y font-sans leading-relaxed" />
              <Button onClick={() => copier(b.val, `kit-${b.cle}`)}
                      className="absolute top-2 right-2 h-7 px-2 bg-hover-strong hover:bg-line-strong text-ink rounded text-xs">
                {copie === `kit-${b.cle}` ? <Check className="h-3.5 w-3.5" /> : <><Copy className="h-3.5 w-3.5 mr-1" />{fr ? "Copier" : "Copy"}</>}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {etat.filleuls.length > 0 && (
        <div className="border-t border-line pt-5">
          <div className="text-xs uppercase tracking-widest text-ink-faint mb-3">
            {fr ? "Vos filleuls" : "Your referrals"}
          </div>
          <ul className="space-y-2.5">
            {etat.filleuls.map((f, i) => (
              <li key={i} className="flex items-center justify-between gap-4 text-sm">
                <div className="min-w-0">
                  <div className={f.actif ? "truncate" : "truncate text-ink-faint line-through"}>
                    {f.entreprise || f.courriel}
                  </div>
                  <div className="text-xs text-ink-faint">
                    {(fr ? f.plan_nom : f.plan_nom_en) || f.plan}
                    {f.annuel && (fr ? " · à l'année" : " · yearly")}
                  </div>
                </div>
                <span className={`shrink-0 font-bold ${f.actif ? "text-brand-ink" : "text-ink-faint"}`}>
                  {f.actif ? `+ ${argent(f.credit)}${fr ? " / mois" : " / mo"}` : (fr ? "Inactif" : "Inactive")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
